import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import { appPath } from '../lib/config';
import '../components/landing.css';
import './getting-started.css';

const PAGE_TITLE = 'How to read the Paddiq Leaderboard | Paddiq';

export function LeaderboardGuidePage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">Performance guide</p>
            <h1>Track your fastest laps with the Leaderboard.</h1>
            <p className="guide-hero__lead">
              See your team&apos;s quickest recorded laps by circuit, understand each time gap, and
              check the vehicle setup attached to a session.
            </p>
            <div className="guide-hero__actions">
              <a
                className="landing-btn landing-btn--primary landing-btn--lg"
                href={appPath('/leaderboard')}
              >
                Open Leaderboard
              </a>
              <a
                className="landing-btn landing-btn--ghost landing-btn--lg"
                href="/resources/compare-laps-and-traces"
              >
                Compare laps and traces
              </a>
            </div>
            <p className="guide-hero__meta">Web guide · Reviewed October 1, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/use-leaderboard" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#open">Open the Leaderboard</a>
                </li>
                <li>
                  <a href="#rows">Read a circuit card</a>
                </li>
                <li>
                  <a href="#gaps">Understand time gaps</a>
                </li>
                <li>
                  <a href="#setup">Check the vehicle setup</a>
                </li>
                <li>
                  <a href="#missing">Restore missing results</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>At a glance</strong>
                <span>
                  Each session contributes its fastest usable lap. Paddiq shows up to ten results
                  for each circuit.
                </span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="open">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">Find your results</p>
                  <h2>Open Leaderboard after uploading a session.</h2>
                  <ol className="guide-instructions">
                    <li>
                      On the web, choose <strong>Leaderboard</strong> in the main navigation.
                    </li>
                    <li>
                      Find the circuit card you want to review, then check each session’s saved
                      layout before comparing times.
                    </li>
                    <li>
                      Start with the most recent circuits at the top, then scroll for earlier
                      activity.
                    </li>
                  </ol>
                  <div className="guide-callout">
                    Leaderboard results appear after Paddiq has a usable lap and can match the
                    session to a saved circuit.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="rows">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">Circuit card</p>
                  <h2>Read the fastest sessions from top to bottom.</h2>
                  <ol className="guide-instructions">
                    <li>
                      The card heading shows the circuit, number of recorded sessions, and best lap.
                    </li>
                    <li>The numbered circle shows the result&apos;s rank for that circuit.</li>
                    <li>
                      The row shows the lap time, session date, lap number, vehicle, and driver when
                      those details are available.
                    </li>
                    <li>
                      The first row is labeled <strong>Personal best</strong>. Later rows show their
                      gap to that leading time.
                    </li>
                  </ol>
                  <p>
                    Paddiq uses the quickest usable lap from each session and lists up to ten
                    results for the circuit.
                  </p>
                </div>
              </section>

              <section className="guide-step" id="gaps">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">Time difference</p>
                  <h2>Use the gap to measure progress at the same circuit.</h2>
                  <ol className="guide-instructions">
                    <li>Read the leading time at rank 1.</li>
                    <li>
                      Read each <strong>+</strong> value as seconds behind that time. For example,{' '}
                      <strong>+0.012</strong> is twelve thousandths of a second.
                    </li>
                    <li>
                      Compare entries only when the sessions used the same layout and direction. A
                      circuit card alone does not establish that every run used the same
                      configuration.
                    </li>
                    <li>
                      Open <strong>Sessions</strong> when you want to compare the laps on the map or
                      in telemetry traces.
                    </li>
                  </ol>
                </div>
              </section>

              <figure className="guide-workflow guide-workflow--leaderboard">
                <div
                  className="guide-leaderboard-scroll"
                  tabIndex={0}
                  aria-label="Scrollable Paddiq Leaderboard circuit card"
                >
                  <img
                    src="/leaderboard-circuit-setup.webp"
                    width="760"
                    height="293"
                    alt="Paddiq Leaderboard circuit card showing two ranked lap times and an expanded vehicle setup"
                  />
                </div>
                <figcaption>
                  Example circuit card. The setup shown is labeled CURRENT (NOT DATED TO SESSION):
                  it is the vehicle’s current setup, not a record of what was used for either lap.
                </figcaption>
              </figure>

              <section className="guide-step" id="setup">
                <div className="guide-step__number" aria-hidden="true">
                  4
                </div>
                <div>
                  <p className="guide-step__eyebrow">Vehicle context</p>
                  <h2>Expand a row before comparing setups.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Select a row or its <strong>Show setup</strong> control.
                    </li>
                    <li>
                      Review the available track widths, seat position, gearing, tire pressures, and
                      weight.
                    </li>
                    <li>
                      Treat <strong>KART SETUP · LOGGED</strong> as a dated setup record associated
                      with that session.
                    </li>
                    <li>
                      Treat <strong>CURRENT (NOT DATED TO SESSION)</strong> as the vehicle&apos;s
                      current setup, not proof of what was used for that lap.
                    </li>
                  </ol>
                  <div className="guide-callout">
                    <strong>No setup</strong> means Paddiq does not have setup details to show for
                    that entry. The lap time can still appear.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="missing">
                <div className="guide-step__number" aria-hidden="true">
                  5
                </div>
                <div>
                  <p className="guide-step__eyebrow">Result quality</p>
                  <h2>Complete the session context when a result looks incomplete.</h2>
                  <ol className="guide-instructions">
                    <li>
                      In <strong>Sessions</strong>, open <strong>Session &amp; laps</strong> and
                      make the affected run primary.
                    </li>
                    <li>
                      Expand <strong>Assignments</strong> and confirm the Driver and Vehicle, then
                      choose <strong>Save session details</strong>.
                    </li>
                    <li>
                      If the circuit is missing or incorrect, complete the circuit and start/finish
                      setup.
                    </li>
                    <li>
                      Return to <strong>Leaderboard</strong> and review the result again.
                    </li>
                  </ol>
                </div>
              </section>

              <section className="guide-troubleshooting" id="troubleshooting">
                <p className="guide-step__eyebrow">Troubleshooting</p>
                <h2>If a time or setup is missing</h2>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>No lap data yet</h3>
                    <p>
                      Upload a MyChron session with usable laps. Start with the{' '}
                      <a className="guide-text-link" href="/resources/upload-mychron-session">
                        MyChron upload guide
                      </a>
                      .
                    </p>
                  </article>
                  <article>
                    <h3>The circuit card is missing</h3>
                    <p>
                      Match the session to the correct circuit and complete its lap setup. Use the{' '}
                      <a className="guide-text-link" href="/resources/set-up-circuit-laps">
                        circuit lap setup guide
                      </a>
                      .
                    </p>
                  </article>
                  <article>
                    <h3>The driver or vehicle is missing</h3>
                    <p>
                      Correct the Driver and Vehicle in <strong>Assignments</strong>. The result can
                      show <strong>Kart unknown</strong> until a vehicle is available.
                    </p>
                  </article>
                  <article>
                    <h3>The setup may not match the day</h3>
                    <p>
                      Check the setup heading. Do not use{' '}
                      <strong>CURRENT (NOT DATED TO SESSION)</strong> as a historical setup record.
                    </p>
                  </article>
                </div>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Review your progress</p>
                  <h2>Start with one circuit and one time gap.</h2>
                  <p>
                    Use Leaderboard to find the sessions worth a closer comparison, then open
                    Sessions to understand where the lap time changed.
                  </p>
                </div>
                <div className="guide-cta__actions">
                  <a
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    href={appPath('/leaderboard')}
                  >
                    Open Leaderboard
                  </a>
                  <a
                    className="landing-btn landing-btn--ghost landing-btn--lg"
                    href="/resources/compare-laps-and-traces"
                  >
                    Compare laps and traces
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
